import React from "react";
import { Table } from "reactstrap";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import Api from "services/api";
import { formatServerDateTime } from "helpers/formatServerDateTime";
import FullPageLoader from "components/common/FullPageLoader";
import { SubmitButton } from "components/common/buttons/SubmitButton";
import Pagination from "components/Pagination";
import ApiLogDetailModal from "components/common/ApiLogDetailModal";

const STATUS_STYLES = {
  pending: { background: "#ffb82a33", color: "#7a4f01" },
  retrying: { background: "#fd7e1433", color: "#c05600" },
  sent: { background: "#1c7c3633", color: "#1c7c36" },
  failed: { background: "#e33a3133", color: "#b51a12" },
  superseded: { background: "#80808033", color: "#555" },
  skipped: { background: "#80808033", color: "#555" },
};

const StatusBadge = ({ status }) => (
  <span
    style={{
      display: "inline-block",
      padding: "1px 10px",
      fontSize: 12,
      fontWeight: 600,
      borderRadius: 10,
      textTransform: "capitalize",
      ...(STATUS_STYLES[status] || STATUS_STYLES.superseded),
    }}
  >
    {status}
  </span>
);

const ApiLogs = () => {
  const [appointmentId, setAppointmentId] = React.useState("");
  const [startDate, setStartDate] = React.useState("");
  const [endDate, setEndDate] = React.useState("");
  const [rows, setRows] = React.useState([]);
  const [meta, setMeta] = React.useState({ total: 0, last_page: 1, current_page: 1, per_page: 25 });
  const [loading, setLoading] = React.useState(false);
  const [detailLogId, setDetailLogId] = React.useState(null);

  const params = (page) => ({
    appointment_id: appointmentId.trim(),
    start_date: startDate,
    end_date: endDate,
    pageLength: 25,
    page,
  });

  const fetchLogs = async (page = 1) => {
    setLoading(true);
    const res = await Api.fetchApiLogs(page, params(page));
    setLoading(false);
    if (res?.status >= 200 && res?.status < 300) {
      const data = res?.data?.data;
      setRows(data?.data || []);
      setMeta({
        total: data?.total || 0,
        last_page: data?.last_page || 1,
        current_page: data?.current_page || 1,
        per_page: data?.per_page || 25,
      });
    } else {
      toast.error(res?.data?.message || "Could not load API logs.", { className: "errorToast" });
    }
  };

  React.useEffect(() => {
    fetchLogs(1);
  }, []);

  const handleReset = () => {
    setAppointmentId("");
    setStartDate("");
    setEndDate("");
    setLoading(true);
    Api.fetchApiLogs(1, { pageLength: 25, page: 1 }).then((res) => {
      setLoading(false);
      if (res?.status >= 200 && res?.status < 300) {
        const data = res?.data?.data;
        setRows(data?.data || []);
        setMeta({ total: data?.total || 0, last_page: data?.last_page || 1, current_page: data?.current_page || 1, per_page: data?.per_page || 25 });
      }
    });
  };

  return (
    <div className="content">
      {loading && <FullPageLoader />}
      <div className="row_main_head">
        <h2 className="main_heading">API Logs</h2>
      </div>

      <div className="card_table p-4">
        <div className="row-primary row-r" style={{ alignItems: "flex-end", gap: 16, flexWrap: "wrap" }}>
          <div className="row-primary">
            <h2>Appointment ID</h2>
            <input
              className="input-in type_inp"
              type="text"
              placeholder="e.g. A222905"
              value={appointmentId}
              onChange={(e) => setAppointmentId(e.target.value)}
            />
          </div>
          <div className="date row-primary">
            <h2>Start Date</h2>
            <div className="input-image">
              <input type="date" className="input-in type_inp" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
          </div>
          <div className="date row-primary">
            <h2>End Date</h2>
            <div className="input-image">
              <input type="date" className="input-in type_inp" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <SubmitButton buttonType="button" btnText="Search" className="green" handleSubmit={() => fetchLogs(1)} />
            <SubmitButton buttonType="button" btnText="Reset" className="lightBlue" handleSubmit={handleReset} />
          </div>
        </div>
      </div>

      <div className="card_table p-4">
        <p style={{ color: "#809FB8", marginBottom: 8 }}>{meta.total} record{meta.total === 1 ? "" : "s"}</p>
        <div className="table_responsive">
          <Table>
            <thead className="text-primary">
              <tr>
                <th>#</th>
                <th>Event Type</th>
                <th>Job</th>
                <th>Status</th>
                <th>Response</th>
                <th>Correlation ID</th>
                <th>Error</th>
                <th>Created At</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={9} style={{ textAlign: "center" }}>No API logs found</td></tr>
              ) : rows.map((log, i) => (
                <tr key={log.id}>
                  <td>{(meta.current_page - 1) * meta.per_page + i + 1}</td>
                  <td style={{ whiteSpace: "nowrap" }}>{log.event_type}</td>
                  <td>
                    {log.appointment?.appid ? (
                      <Link className="link_preview" to={`/appointments/view/${log.appointment_id}`}>{log.appointment.appid}</Link>
                    ) : (log.appointment_id || "-")}
                  </td>
                  <td><StatusBadge status={log.status} /></td>
                  <td>{log.response_code ?? "-"}</td>
                  <td>{log.correlation_id || "-"}</td>
                  <td title={log.error || ""} style={{ maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {log.error || "-"}
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>{formatServerDateTime(log.created_at)}</td>
                  <td>
                    <span
                      className="link_preview"
                      style={{ cursor: "pointer" }}
                      onClick={() => setDetailLogId(log.id)}
                    >
                      View
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
        <Pagination
          totalItems={meta.total}
          currentPage={meta.current_page}
          pageLength={meta.per_page}
          totalPages={meta.last_page}
          handleChange={(page) => fetchLogs(page)}
          prevDisabled={meta.last_page <= 1 || meta.current_page === 1}
          nextDisabled={meta.last_page <= 1 || meta.current_page === meta.last_page}
        />
      </div>

      <ApiLogDetailModal
        logId={detailLogId}
        isOpen={detailLogId !== null}
        toggle={() => setDetailLogId(null)}
      />
    </div>
  );
};

export default ApiLogs;
